import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  PLAYER_CLASS_PROFILES,
  PlayerClasses,
  TransitionEffects,
  type NameI18n,
  type PlayerClass,
  type ProgramItemUpsert,
  type ProgramUpsert,
  type ResourceState,
  type ResourceSummary,
  type Transition,
  type TransitionEffect,
} from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/icon';
import { pickName } from '@/lib/i18n-name';
import { STATE_BADGE_VARIANT } from '@/lib/state-badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TransitionControl,
  effectiveTransition,
} from '@/components/composer/transition-control';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/auth-context';
import {
  FULL_ZONE_ID,
  SINGLETON_LAYOUT_ID,
  activateComposerVersion,
  getCapabilityMatrix,
  getComposer,
  getComposerLayout,
  permissionForAction,
  replacePrograms,
  runAuditAction,
  setComposerBackgroundAudio,
  visibleAuditActionsForState,
  type ComposerAuditAction,
  type ComposerDetail,
} from '@/api/composers';
import { ClassMismatchChip } from './design-class';
import { listResources } from '@/api/resources';
import { CapabilityMatrix } from '@/components/CapabilityMatrix';
import { MediaPreview } from '@/components/media/MediaPreview';
import { useRegisterSave } from './composer-save-registry';

/**
 * `/composers/:id` — composer detail editor (Task 27).
 *
 * Header: name, state badge, version number.
 * Program editor: Phase-1 supports a single full-screen program; we render
 *   the program's items as a reorderable list with thumbnail, name, and
 *   per-item duration (UX-seconds, stored as ms). HTML5 drag-and-drop —
 *   `@dnd-kit` is not installed and adding it is out of scope.
 * Resource picker: modal that lists `state=approved` resources of kind
 *   image/video/audio. The server's `kind` filter is single-valued, so we
 *   pull approved-only and filter client-side to the three eligible kinds.
 * Audit drawer: state-gated buttons for the canonical actions. Server
 *   authoritative; UI gating is UX only.
 */

const DEFAULT_ITEM_DURATION_MS = 10_000;

// In-UI representation of a program item — keeps a stable local id so React
// reconciles drag-reorders without remounting.
interface EditableItem {
  localId: string;
  /** Resource version id the item points at. */
  resource_version_id: string;
  /** Display fields, cached from the picker. */
  resource_name: NameI18n;
  resource_kind: 'image' | 'video' | 'audio' | 'rss_feed' | 'web_page';
  duration_ms: number;
  /** Per-item transition override (managed in the layout editor). Preserved
   *  here so a "Save programs" never wipes it. */
  transition?: Transition;
}

interface EditableProgram {
  localId: string;
  /** Server id when known; undefined for newly-added programs. */
  serverId?: string;
  items: EditableItem[];
  /** Loop default transition for this program (the per-loop setting). */
  defaultTransition?: Transition;
}

let localCounter = 0;
function nextLocalId(prefix: string): string {
  localCounter += 1;
  return `${prefix}-${localCounter}`;
}

// SP3c follow-up (real programs dirty-tracking): canonical serialization of
// the SAVE-RELEVANT program fields only — program order, per-loop default
// transition, and each item's resource-version id / duration / transition.
// Display-only fields (localId, resource_name, resource_kind) and serverId
// are EXCLUDED: they never reach the replacePrograms payload. This function
// exists purely for baseline COMPARISON — the payload builder in saveMutation
// is untouched (DS §5).
function serializePrograms(programs: ReadonlyArray<EditableProgram>): string {
  return JSON.stringify(
    programs.map((p) => ({
      dt: p.defaultTransition
        ? { e: p.defaultTransition.effect, d: p.defaultTransition.duration_ms }
        : null,
      items: p.items.map((it) => ({
        rv: it.resource_version_id,
        d: it.duration_ms,
        tr: it.transition
          ? { e: it.transition.effect, d: it.transition.duration_ms }
          : null,
      })),
    })),
  );
}

// P3 W2 B10 (FS §7) — Class-C high-contrast LED-sim preview filter.
//
// The simulated LED panel of a Class-C controller is given a quick visual
// sanity check via a CSS `filter` chain (contrast bump + slight
// desaturation + dim) — this is intentionally a "what would this read
// like on an LED panel" approximation, not a per-pixel pitch shader
// (those are deferred to P8, FS §7 out-of-scope). The default class is
// `A` (kiosque navigateur, the most permissive profile) so a freshly
// opened composer shows the asset unmodified; A/B/D apply no filter, so
// the default is also the no-filter case.
const PREVIEW_CLASS_C_FILTER =
  'contrast(1.4) saturate(0.8) brightness(0.95)';

export function ComposerDetailPage({
  embedded = false,
}: { embedded?: boolean } = {}): JSX.Element {
  const { t, i18n } = useTranslation(['composers', 'common']);
  const params = useParams<{ id: string }>();
  const composerId = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const lang: 'fr' | 'en' = i18n.language === 'en' ? 'en' : 'fr';

  // Preview-class selector for the Class-C LED-sim. Default `A` → no filter.
  const [previewClass, setPreviewClass] = useState<PlayerClass>('A');

  const detailQuery = useQuery<ComposerDetail>({
    queryKey: ['composers', 'detail', composerId],
    queryFn: () => getComposer(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  const matrixQuery = useQuery({
    queryKey: ['composers', 'capability-matrix', composerId],
    queryFn: () => getCapabilityMatrix(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  // The current layout's zones — items default to the first zone so a
  // program save targets a real zone on a saved multi-zone layout (rather
  // than the hardcoded 'full', which may not exist after a layout edit).
  const layoutQuery = useQuery({
    queryKey: ['composers', 'layout', composerId],
    queryFn: () => getComposerLayout(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  // P3 W2 B7 (DS §2) — the W1 capability boundary. A pre-P3 composer that
  // later gained a P3 capability-specific active version leaves legacy-flags
  // players on `assignment.unavailable` (resolved_version_id === null) until
  // its default/universal version is explicitly (re)activated. We surface a
  // one-click "activate the default version" affordance ONLY when there is
  // genuinely an unavailable player AND a default-profile version to
  // activate — never a no-op action.
  const matrix = matrixQuery.data;
  const defaultVersion = matrix?.versions.find((v) => v.is_default_profile);
  const hasUnavailable = !!matrix?.players.some(
    (p) => p.resolved_version_id === null,
  );

  const canActivateDefault = hasPermission('composers.approve');
  const [activateMessageKey, setActivateMessageKey] = useState<
    'composers:matrix.activate_default_success' | null
  >(null);

  const activateDefaultMutation = useMutation<ComposerDetail, ApiError, void>({
    mutationFn: () => {
      if (!defaultVersion) {
        throw new Error('no default-profile version to activate');
      }
      return activateComposerVersion(
        composerId,
        defaultVersion.composer_version_id,
      );
    },
    onSuccess: () => {
      setActivateMessageKey('composers:matrix.activate_default_success');
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'capability-matrix', composerId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'detail', composerId],
      });
    },
  });

  // Local editable program state — initialized from server data once it
  // arrives, then mutated client-side until "Save programs" pushes it back.
  const [programs, setPrograms] = useState<EditableProgram[]>([]);
  const initRef = useRef<string | null>(null);

  // SP3c follow-up: last-saved baseline for real dirty-tracking. Set when the
  // server data seeds `programs` and re-set after every successful save;
  // `programsChanged` below is a pure serialize-and-compare against it.
  const [programsBaseline, setProgramsBaseline] = useState<string | null>(null);

  useEffect(() => {
    const data = detailQuery.data;
    if (!data) return;
    if (initRef.current === data.id) return;
    initRef.current = data.id;
    const inited: EditableProgram[] = data.programs.map((p) => ({
      localId: nextLocalId('prog'),
      serverId: p.id,
      ...(p.default_transition ? { defaultTransition: p.default_transition } : {}),
      items: p.items
        // W8 T2: only image/video items are editable via the detail page's
        // legacy resource-picker UI. Other binding kinds (web_page, rss,
        // composer, empty) are managed via the layout editor's ZoneBindingPicker.
        .filter((it) => it.kind === 'image' || it.kind === 'video')
        .map((it) => {
          // Safe: filter above guarantees resource_version_id exists.
          const rvId = 'resource_version_id' in it ? it.resource_version_id : '';
          return {
            localId: nextLocalId('item'),
            resource_version_id: rvId,
            // P1 limitation: the server's `ProgramItemSummary` does not carry
            // the resource's name or kind. We show the version-id prefix as a
            // placeholder; items added via the picker carry the real name.
            resource_name: { fr: rvId.slice(0, 8) },
            resource_kind: 'image',
            duration_ms: it.duration_ms,
            ...(it.transition ? { transition: it.transition } : {}),
          };
        }),
    }));
    setPrograms(inited);
    setProgramsBaseline(serializePrograms(inited));
  }, [detailQuery.data]);

  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);
  const [saveMessageKey, setSaveMessageKey] = useState<
    'composers:detail.save_success' | 'composers:detail.save_error' | null
  >(null);

  // SP3c follow-up: the serialization of the programs a save is persisting —
  // committed to `programsBaseline` on success so dirty flips back to false.
  // A ref (not state) because it's write-at-save/read-at-success bookkeeping.
  const pendingBaselineRef = useRef<string | null>(null);

  const saveMutation = useMutation<void, ApiError, void>({
    mutationFn: async () => {
      // Baseline bookkeeping only — the payload below is byte-identical
      // to what this mutation always sent (DS §5).
      pendingBaselineRef.current = serializePrograms(programs);
      // Server ignores layout_id (geometry is owned by the layout editor)
      // but preserves the version's current layout. Target the layout's
      // first zone so items land on a real zone after a multi-zone save.
      // Note: zones?.[0] (not zones[0]) — guard the index too, since data
      // may be present without a well-formed zones array.
      const defaultZoneId = layoutQuery.data?.zones?.[0]?.id ?? FULL_ZONE_ID;
      const payload: ProgramUpsert[] = programs.map((prog, progIdx) => ({
        order_idx: progIdx,
        layout_id: SINGLETON_LAYOUT_ID,
        ...(prog.defaultTransition ? { default_transition: prog.defaultTransition } : {}),
        items: prog.items.map((it, itemIdx): ProgramItemUpsert => ({
          order_idx: itemIdx,
          resource_version_id: it.resource_version_id,
          duration_ms: it.duration_ms,
          zone_id: defaultZoneId,
          ...(it.transition ? { transition: it.transition } : {}),
        })),
      }));
      await replacePrograms(composerId, payload);
    },
    onSuccess: () => {
      setSaveMessageKey('composers:detail.save_success');
      if (pendingBaselineRef.current !== null) {
        setProgramsBaseline(pendingBaselineRef.current);
      }
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'detail', composerId],
      });
      void queryClient.invalidateQueries({ queryKey: ['composers', 'list'] });
    },
    onError: () => {
      setSaveMessageKey('composers:detail.save_error');
    },
  });

  const handlePicked = useCallback(
    (programLocalId: string, resource: ResourceSummary) => {
      if (!resource.current_version_id) return;
      setPrograms((prev) =>
        prev.map((p) =>
          p.localId === programLocalId
            ? {
                ...p,
                items: [
                  ...p.items,
                  {
                    localId: nextLocalId('item'),
                    resource_version_id: resource.current_version_id!,
                    resource_name: resource.name_i18n,
                    resource_kind: resource.kind,
                    duration_ms: DEFAULT_ITEM_DURATION_MS,
                  },
                ],
              }
            : p,
        ),
      );
      setPickerOpenFor(null);
    },
    [],
  );

  const ensureFirstProgram = useCallback((): string => {
    if (programs.length > 0) return programs[0]!.localId;
    const created: EditableProgram = {
      localId: nextLocalId('prog'),
      items: [],
    };
    setPrograms([created]);
    return created.localId;
  }, [programs]);

  const handleAddItemClick = useCallback(() => {
    const id = ensureFirstProgram();
    setPickerOpenFor(id);
  }, [ensureFirstProgram]);

  const handleRemoveItem = useCallback(
    (programLocalId: string, itemLocalId: string) => {
      setPrograms((prev) =>
        prev.map((p) =>
          p.localId === programLocalId
            ? {
                ...p,
                items: p.items.filter((it) => it.localId !== itemLocalId),
              }
            : p,
        ),
      );
    },
    [],
  );

  const handleDurationChange = useCallback(
    (programLocalId: string, itemLocalId: string, seconds: number) => {
      const ms = Math.max(0, Math.round(seconds * 1000));
      setPrograms((prev) =>
        prev.map((p) =>
          p.localId === programLocalId
            ? {
                ...p,
                items: p.items.map((it) =>
                  it.localId === itemLocalId ? { ...it, duration_ms: ms } : it,
                ),
              }
            : p,
        ),
      );
    },
    [],
  );

  const handleReorder = useCallback(
    (programLocalId: string, fromIdx: number, toIdx: number) => {
      setPrograms((prev) =>
        prev.map((p) => {
          if (p.localId !== programLocalId) return p;
          if (fromIdx === toIdx) return p;
          const next = p.items.slice();
          const [moved] = next.splice(fromIdx, 1);
          if (moved) next.splice(toIdx, 0, moved);
          return { ...p, items: next };
        }),
      );
    },
    [],
  );

  const canSave = useMemo(() => {
    // Server requires programs.min(1) and items.min(1) per program; gate
    // accordingly to avoid 400s.
    if (programs.length === 0) return false;
    if (programs.some((p) => p.items.length === 0)) return false;
    return hasPermission('composers.update');
  }, [programs, hasPermission]);

  // SP3c follow-up: REAL dirty-tracking — serialize the save-relevant program
  // fields and compare against the last-saved baseline (seeded from server
  // data, re-committed after each successful save). Replaces the v1
  // `dirty: canSave` coarse flag.
  const programsChanged = useMemo(
    () =>
      programsBaseline !== null &&
      serializePrograms(programs) !== programsBaseline,
    [programs, programsBaseline],
  );

  // SP3c: when embedded in the tabbed ComposerEditor, register the programs
  // save with the unified "Enregistrer la composition" orchestrator (no-ops
  // standalone — no provider). `dirty` is true only for a genuinely edited AND
  // saveable program list (`canSave` keeps the server's min-1-item invariant —
  // never enable a save that would 400).
  useRegisterSave({
    key: 'programs',
    order: 1,
    label: t('composers:tab_content', { defaultValue: 'Contenu' }),
    dirty: programsChanged && canSave,
    save: () => saveMutation.mutateAsync(),
  });

  if (detailQuery.isLoading) {
    return (
      <p
        role="status"
        data-testid="composer-detail-loading"
        className="text-sm text-muted-foreground"
      >
        {t('composers:detail.loading')}
      </p>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert variant="danger" data-testid="composer-detail-error">
        {t('composers:detail.load_error')}
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const versionText =
    detail.current_version_id === null
      ? t('composers:detail.version_unsaved')
      : t('composers:detail.version_label', {
          version:
            typeof detail.current_version_number === 'number'
              ? detail.current_version_number
              : '—',
        });

  return (
    <div className="flex flex-col gap-6" data-testid="composer-detail-page">
      {/* SP3c: the page header (state/version/save) is hidden when embedded in
          the tabbed ComposerEditor — the shell provides the tabs + the unified
          "Enregistrer la composition" button, and the editor hero owns the
          rename affordance, version label and class-mismatch chip. This
          standalone header only exists so the page stays mountable on its own
          (tests mount it directly). */}
      {!embedded && (
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate('/composers')}
            data-testid="composer-detail-back"
          >
            <Icon name="chevron-left" size={16} aria-hidden />
            {t('common:back')}
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {pickName(detail.name_i18n, lang)}
          </h1>
          {hasPermission('composers.update') && (
            <Button
              type="button"
              size="sm"
              onClick={() => navigate(`/composers/${composerId}/layout`)}
              data-testid="composer-open-layout-btn"
            >
              {t('composers:layout_editor.open_button', {
                defaultValue: 'Éditer la mise en page',
              })}
            </Button>
          )}
          <Badge
            variant={STATE_BADGE_VARIANT[detail.state] ?? 'neutral'}
            data-testid={`composer-state-badge-${detail.state}`}
          >
            {t(`composers:state.${detail.state}`)}
          </Badge>
          <span
            className="text-sm text-muted-foreground"
            data-testid="composer-version"
          >
            {versionText}
          </span>
          <ClassMismatchChip matrix={matrix} lang={lang} />
        </div>
      </header>
      )}

      <section
        aria-label={t('composers:detail.programs_title')}
        className="flex flex-col gap-4 rounded-md border border-border bg-card p-4"
      >
        <header className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Icon name="film" size={18} aria-hidden className="text-muted-foreground" />
            {t('composers:detail.programs_title')}
          </h2>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={handleAddItemClick}
              disabled={!hasPermission('composers.update')}
              data-testid="composer-add-item"
            >
              <Icon name="plus" size={16} aria-hidden />
              {t('composers:detail.add_item')}
            </Button>
            {/* Save is blocked until the layout query settles so each item's
                zone_id resolves to a real zone (layoutQuery.data) and never a
                stale FULL_ZONE_ID guess that could orphan items on a multi-zone
                layout. */}
            {/* SP3c: hidden when embedded — the shell's unified "Enregistrer la
                composition" button drives this save (via the save registry). */}
            {!embedded && (
              <Button
                type="button"
                className="gap-2"
                onClick={() => saveMutation.mutate()}
                disabled={
                  !canSave || saveMutation.isPending || layoutQuery.isPending
                }
                data-testid="composer-save-programs"
              >
                <Icon name="check" size={16} aria-hidden />
                {saveMutation.isPending
                  ? t('composers:detail.saving')
                  : t('composers:detail.save')}
              </Button>
            )}
          </div>
        </header>

        {programs.length === 0 ? (
          <p
            data-testid="composer-no-programs"
            className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
          >
            {t('composers:detail.no_programs')}
          </p>
        ) : (
          programs.map((program, idx) => (
            <ProgramBlock
              key={program.localId}
              program={program}
              index={idx + 1}
              lang={lang}
              onReorder={(from, to) => handleReorder(program.localId, from, to)}
              onRemoveItem={(itemId) =>
                handleRemoveItem(program.localId, itemId)
              }
              onDurationChange={(itemId, seconds) =>
                handleDurationChange(program.localId, itemId, seconds)
              }
              onAddItem={() => setPickerOpenFor(program.localId)}
              onDefaultTransitionChange={(t) =>
                setPrograms((prev) =>
                  prev.map((p) => {
                    if (p.localId !== program.localId) return p;
                    if (t === undefined) {
                      // omit the key (exactOptionalPropertyTypes: no present-undefined)
                      const { defaultTransition: _omit, ...rest } = p;
                      return rest;
                    }
                    return { ...p, defaultTransition: t };
                  }),
                )
              }
              onItemTransitionChange={(itemId, tr) =>
                setPrograms((prev) =>
                  prev.map((p) =>
                    p.localId === program.localId
                      ? {
                          ...p,
                          items: p.items.map((it) => {
                            if (it.localId !== itemId) return it;
                            if (tr === undefined) {
                              // omit the key (exactOptionalPropertyTypes)
                              const { transition: _omit, ...rest } = it;
                              return rest;
                            }
                            return { ...it, transition: tr };
                          }),
                        }
                      : p,
                  ),
                )
              }
              canEdit={hasPermission('composers.update')}
            />
          ))
        )}

        {saveMessageKey && (
          <p
            role="status"
            data-testid="composer-save-message"
            className={
              saveMessageKey === 'composers:detail.save_success'
                ? 'rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'
                : 'rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive'
            }
          >
            {t(saveMessageKey)}
          </p>
        )}
      </section>

      <BackgroundMusicSection
        composerId={composerId}
        currentRvId={detail.background_audio_resource_version_id ?? null}
        editable={
          hasPermission('composers.update') &&
          (detail.state === 'draft' || detail.state === 'pending_review')
        }
        lang={lang}
      />

      <section
        data-testid="composer-capability-section"
        aria-label={t('composers:matrix.title')}
        className="flex flex-col gap-4 rounded-md border border-border bg-card p-4"
      >
        <header>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Icon name="grid-3x3" size={18} aria-hidden className="text-muted-foreground" />
            {t('composers:matrix.title')}
          </h2>
        </header>

        {matrixQuery.isLoading ? (
          <p
            role="status"
            data-testid="composer-capability-loading"
            className="text-sm text-muted-foreground"
          >
            {t('composers:matrix.loading')}
          </p>
        ) : matrixQuery.isError || !matrixQuery.data ? (
          <Alert variant="danger" data-testid="composer-capability-error">
            {t('composers:matrix.load_error')}
          </Alert>
        ) : matrixQuery.data.players.length === 0 ? (
          <p
            data-testid="composer-capability-empty"
            className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
          >
            {t('composers:matrix.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <CapabilityMatrix data={matrixQuery.data} />
          </div>
        )}

        {defaultVersion && hasUnavailable && canActivateDefault && (
          <div
            data-testid="composer-activate-default-panel"
            className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-3"
          >
            <p className="text-sm text-amber-900">
              {t('composers:matrix.activate_default_explainer')}
            </p>
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => activateDefaultMutation.mutate()}
                disabled={activateDefaultMutation.isPending}
                data-testid="composer-activate-default"
              >
                {t('composers:matrix.activate_default_button')}
              </Button>
            </div>
            {activateMessageKey && (
              <p
                role="status"
                data-testid="composer-activate-default-message"
                className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
              >
                {t(activateMessageKey)}
              </p>
            )}
          </div>
        )}
      </section>

      <section
        data-testid="composer-preview-section"
        aria-label={t('composers:preview.title')}
        className="flex flex-col gap-3 rounded-md border border-border bg-card p-4"
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Icon name="monitor" size={18} aria-hidden className="text-muted-foreground" />
            {t('composers:preview.title')}
          </h2>
          <label className="flex items-center gap-2 text-sm">
            <span className="font-medium">
              {t('composers:preview.class_label')}
            </span>
            <Select
              value={previewClass}
              onValueChange={(v) => setPreviewClass(v as PlayerClass)}
            >
              <SelectTrigger className="h-8" data-testid="preview-class-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PlayerClasses.map((c) => (
                  <SelectItem key={c} value={c}>
                    {PLAYER_CLASS_PROFILES[c].label_i18n[lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </header>

        <div
          data-testid="composer-preview"
          aria-label={t('composers:preview.canvas_label')}
          style={{
            filter:
              previewClass === 'C' ? PREVIEW_CLASS_C_FILTER : undefined,
          }}
          className="flex h-40 items-center justify-center overflow-hidden rounded-md border border-dashed border-border bg-muted/30 text-sm text-muted-foreground"
        >
          {(() => {
            // SP3d (content tab): preview the first visual item of the
            // composition full-frame. The class filter is applied by the
            // container above; MediaPreview degrades gracefully if bytes fail.
            const previewItem = programs
              .flatMap((p) => p.items)
              .find(
                (it) =>
                  it.resource_kind === 'image' || it.resource_kind === 'video',
              );
            if (!previewItem) {
              return <span>{t('composers:preview.empty')}</span>;
            }
            return (
              <MediaPreview
                source="resource"
                resourceVersionId={previewItem.resource_version_id}
                kind={previewItem.resource_kind}
                size="inline"
              />
            );
          })()}
        </div>

        {previewClass === 'C' && PLAYER_CLASS_PROFILES.C.advisory && (
          <p
            data-testid="composer-preview-advisory"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            {PLAYER_CLASS_PROFILES.C.advisory[lang]}
          </p>
        )}
      </section>

      {/* SP3c: in the tabbed editor the audit drawer is the Approbation tab. */}
      {!embedded && <AuditDrawer composerId={composerId} state={detail.state} />}

      {pickerOpenFor !== null && (
        <ResourcePickerDialog
          onClose={() => setPickerOpenFor(null)}
          onSelect={(r) => handlePicked(pickerOpenFor, r)}
          lang={lang}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Program block — items list with drag-reorder + per-item editor
// ---------------------------------------------------------------------------

interface ProgramBlockProps {
  program: EditableProgram;
  index: number;
  lang: 'fr' | 'en';
  onReorder: (fromIdx: number, toIdx: number) => void;
  onRemoveItem: (itemLocalId: string) => void;
  onDurationChange: (itemLocalId: string, seconds: number) => void;
  onAddItem: () => void;
  onDefaultTransitionChange: (t: Transition | undefined) => void;
  onItemTransitionChange: (itemLocalId: string, t: Transition | undefined) => void;
  canEdit: boolean;
}

function ProgramBlock({
  program,
  index,
  lang,
  onReorder,
  onRemoveItem,
  onDurationChange,
  onAddItem,
  onDefaultTransitionChange,
  onItemTransitionChange,
  canEdit,
}: ProgramBlockProps): JSX.Element {
  const { t } = useTranslation(['composers', 'common']);
  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);

  return (
    <article
      data-testid={`composer-program-${program.localId}`}
      className="flex flex-col gap-2 rounded-md border border-border p-3"
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          {t('composers:detail.program_label', { index })}
        </h3>
        {/* Loop default transition (the per-loop setting). Shares the one
            TransitionControl with per-item overrides below — same effect +
            duration UX, so the two can't drift. "Aucune" ⇒ no default (cut). */}
        <div className="flex items-center gap-1.5" data-testid={`program-default-transition-${program.localId}`}>
          <label className="text-xs text-muted-foreground">
            {t('composers:transition_loop_label', { defaultValue: 'Transition de boucle' })}
          </label>
          <TransitionControl
            value={program.defaultTransition}
            mode="default"
            onChange={onDefaultTransitionChange}
            disabled={!canEdit}
          />
        </div>
      </header>

      {program.items.length === 0 ? (
        <p
          data-testid={`program-no-items-${program.localId}`}
          className="text-sm text-muted-foreground"
        >
          {t('composers:detail.no_items')}
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {program.items.map((item, itemIdx) => (
            <li
              key={item.localId}
              data-testid={`program-item-${item.localId}`}
              data-resource-version={item.resource_version_id}
              draggable={canEdit}
              onDragStart={(e) => {
                setDragFromIdx(itemIdx);
                try {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(itemIdx));
                } catch {
                  /* ignore */
                }
              }}
              onDragOver={(e) => {
                if (dragFromIdx === null) return;
                e.preventDefault();
                try {
                  e.dataTransfer.dropEffect = 'move';
                } catch {
                  /* ignore */
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragFromIdx !== null) {
                  onReorder(dragFromIdx, itemIdx);
                  setDragFromIdx(null);
                }
              }}
              onDragEnd={() => setDragFromIdx(null)}
              className="flex items-center gap-3 rounded-md border border-border bg-background p-2"
            >
              <span
                aria-hidden="true"
                title={t('composers:detail.drag_handle')}
                className="cursor-grab select-none px-1 text-muted-foreground"
              >
                ⋮⋮
              </span>
              <MediaPreview
                source="resource"
                resourceVersionId={item.resource_version_id}
                kind={item.resource_kind}
                size="thumb"
              />
              <div className="flex flex-1 flex-col">
                <span className="text-sm font-medium">
                  {pickName(item.resource_name, lang)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {item.resource_version_id.slice(0, 8)}…
                </span>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <span className="sr-only">
                  {t('composers:detail.duration_label')}
                </span>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={Math.round(item.duration_ms / 1000)}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    onDurationChange(
                      item.localId,
                      Number.isFinite(v) ? v : 0,
                    );
                  }}
                  className="h-8 w-20"
                  data-testid={`program-item-duration-${item.localId}`}
                  disabled={!canEdit}
                />
                <span className="text-muted-foreground">
                  {t('common:duration_seconds_suffix', { defaultValue: 's' })}
                </span>
              </label>
              {/* SP3a — per-item transition (now editable here, not only in the
                  layout editor) + the EFFECTIVE transition so "which wins" is
                  always visible. */}
              <div
                className="flex flex-col items-end gap-0.5"
                data-testid={`program-item-transition-${item.localId}`}
              >
                <TransitionControl
                  value={item.transition}
                  mode="item"
                  inheritedFrom={program.defaultTransition ?? null}
                  onChange={(tr) => onItemTransitionChange(item.localId, tr)}
                  disabled={!canEdit}
                />
                <span
                  className="text-[10px] text-muted-foreground"
                  data-testid={`program-item-effective-${item.localId}`}
                >
                  {(() => {
                    const eff = effectiveTransition(item.transition, program.defaultTransition);
                    if (!eff || eff.effect === 'cut') {
                      return t('composers:transition_none', { defaultValue: 'Aucune (coupe)' });
                    }
                    const label = t(`composers:transition_effect_${eff.effect}`, {
                      defaultValue: eff.effect,
                    });
                    return item.transition
                      ? label
                      : `${label} (${t('composers:transition_effective_inherited', { defaultValue: 'hérité' })})`;
                  })()}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemoveItem(item.localId)}
                disabled={!canEdit}
                data-testid={`program-item-remove-${item.localId}`}
              >
                {t('composers:detail.remove_item')}
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={onAddItem}
          disabled={!canEdit}
          data-testid={`program-add-item-${program.localId}`}
        >
          <Icon name="plus" size={16} aria-hidden />
          {t('composers:detail.add_item')}
        </Button>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Background music ("Musique de fond") — composition-level ambient track
// ---------------------------------------------------------------------------
//
// A single approved `audio` resource set on the composer's current version
// (server: PATCH /admin/composers/:id/background-audio). The chosen track is
// previewed inline via the shared MediaPreview; editing is gated on
// composers.update + an editable version state (draft/pending_review) — the
// server is authoritative (validates approved-audio + editable-version, 400/
// 409 otherwise).
interface BackgroundMusicSectionProps {
  composerId: string;
  currentRvId: string | null;
  editable: boolean;
  lang: 'fr' | 'en';
}

function BackgroundMusicSection({
  composerId,
  currentRvId,
  editable,
  lang,
}: BackgroundMusicSectionProps): JSX.Element {
  const { t } = useTranslation(['composers', 'common']);
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [messageKey, setMessageKey] = useState<'success' | 'error' | null>(null);

  // SP3c follow-up: the last REQUESTED background-audio value ({value} so a
  // requested "clear" — value:null — is distinguishable from "nothing
  // pending"). Set on every user action, cleared when the mutation lands.
  const [pending, setPending] = useState<{ value: string | null } | null>(null);

  // Approved audio resources: drives the picker AND resolves the current
  // track's display name from its resource-version id.
  const audioQuery = useQuery({
    queryKey: ['composers', 'bg-audio', 'approved-audio'],
    queryFn: () =>
      listResources({ kind: 'audio', state: 'approved', page: 1, per_page: 100 }),
    staleTime: 15_000,
  });
  const audioItems = audioQuery.data?.items ?? [];
  const current = currentRvId
    ? audioItems.find((r) => r.current_version_id === currentRvId)
    : undefined;

  const mutation = useMutation<ComposerDetail, ApiError, string | null>({
    mutationFn: (rvId) => setComposerBackgroundAudio(composerId, rvId),
    onSuccess: () => {
      setMessageKey('success');
      setPending(null);
      setPickerOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'detail', composerId],
      });
    },
    onError: () => setMessageKey('error'),
  });

  // SP3c follow-up: register with the unified "Enregistrer la composition"
  // orchestrator. Instant-apply stays the primary path (selecting/clearing a
  // track mutates immediately); this registration makes a FAILED instant-apply
  // retryable from the unified save button (and puts the dirty dot on the
  // Contenu tab). DS §5: the save re-invokes the SAME
  // setComposerBackgroundAudio mutation — the payload is untouched.
  useRegisterSave({
    key: 'background_audio',
    order: 4,
    label: t('composers:bg_music.title', { defaultValue: 'Musique de fond' }),
    dirty: pending !== null && mutation.isError,
    save: async () => {
      if (pending) await mutation.mutateAsync(pending.value);
    },
  });

  return (
    <section
      data-testid="composer-bg-music-section"
      aria-label={t('composers:bg_music.title', { defaultValue: 'Musique de fond' })}
      className="flex flex-col gap-3 rounded-md border border-border bg-card p-4"
    >
      <header className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Icon name="play-circle" size={18} aria-hidden className="text-muted-foreground" />
          {t('composers:bg_music.title', { defaultValue: 'Musique de fond' })}
        </h2>
        {editable && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              setMessageKey(null);
              setPickerOpen(true);
            }}
            data-testid="composer-bg-music-choose"
          >
            <Icon name="plus" size={16} aria-hidden />
            {t('composers:bg_music.choose', {
              defaultValue: 'Choisir une musique',
            })}
          </Button>
        )}
      </header>

      <p className="text-sm text-muted-foreground">
        {t('composers:bg_music.help', {
          defaultValue:
            'Une piste audio approuvée, jouée en fond sur toute la composition.',
        })}
      </p>

      {currentRvId ? (
        <div
          data-testid="composer-bg-music-current"
          className="flex items-center gap-3 rounded-md border border-border bg-background p-2"
        >
          <div className="w-40 shrink-0">
            <MediaPreview
              source="resource"
              kind="audio"
              resourceVersionId={currentRvId}
              size="inline"
            />
          </div>
          <span className="flex-1 text-sm font-medium">
            {current
              ? pickName(current.name_i18n, lang)
              : `${currentRvId.slice(0, 8)}…`}
          </span>
          {editable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                setMessageKey(null);
                setPending({ value: null });
                mutation.mutate(null);
              }}
              disabled={mutation.isPending}
              data-testid="composer-bg-music-clear"
            >
              <Icon name="trash-2" size={16} aria-hidden />
              {t('composers:bg_music.clear', { defaultValue: 'Retirer' })}
            </Button>
          )}
        </div>
      ) : (
        <p
          data-testid="composer-bg-music-empty"
          className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground"
        >
          {t('composers:bg_music.empty', {
            defaultValue: 'Aucune musique de fond',
          })}
        </p>
      )}

      {messageKey && (
        <p
          role="status"
          data-testid="composer-bg-music-message"
          className={
            messageKey === 'success'
              ? 'rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'
              : 'rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive'
          }
        >
          {messageKey === 'success'
            ? t('composers:bg_music.saved', { defaultValue: 'Musique de fond mise à jour.' })
            : t('composers:bg_music.error', {
                defaultValue: "Échec de la mise à jour de la musique de fond.",
              })}
        </p>
      )}

      {pickerOpen && (
        <BackgroundMusicPickerDialog
          items={audioItems}
          loading={audioQuery.isLoading}
          lang={lang}
          onClose={() => setPickerOpen(false)}
          onSelect={(r) => {
            if (r.current_version_id) {
              setPending({ value: r.current_version_id });
              mutation.mutate(r.current_version_id);
            }
          }}
        />
      )}
    </section>
  );
}

interface BackgroundMusicPickerDialogProps {
  items: ResourceSummary[];
  loading: boolean;
  lang: 'fr' | 'en';
  onClose: () => void;
  onSelect: (resource: ResourceSummary) => void;
}

function BackgroundMusicPickerDialog({
  items,
  loading,
  lang,
  onClose,
  onSelect,
}: BackgroundMusicPickerDialogProps): JSX.Element {
  const { t } = useTranslation(['composers']);
  // Defensive: the query already filters kind=audio + state=approved, but
  // guard against any non-audio leaking in.
  const audio = items.filter((r) => r.kind === 'audio');

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="composer-bg-music-picker"
        className="flex max-h-[80vh] w-full max-w-2xl flex-col gap-3"
      >
        <DialogHeader>
          <DialogTitle>
            {t('composers:bg_music.picker_title', {
              defaultValue: 'Choisir une musique de fond',
            })}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p
            role="status"
            data-testid="composer-bg-music-picker-loading"
            className="text-sm text-muted-foreground"
          >
            {t('composers:picker.loading', { defaultValue: 'Chargement…' })}
          </p>
        ) : audio.length === 0 ? (
          <p
            data-testid="composer-bg-music-picker-empty"
            className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
          >
            {t('composers:bg_music.picker_empty', {
              defaultValue: 'Aucune piste audio approuvée',
            })}
          </p>
        ) : (
          <ul
            className="flex flex-1 flex-col gap-2 overflow-auto"
            data-testid="composer-bg-music-picker-list"
          >
            {audio.map((r) => (
              <li
                key={r.id}
                data-testid={`composer-bg-music-picker-item-${r.id}`}
                className="flex items-center gap-3 rounded-md border border-border bg-background p-2"
              >
                {r.current_version_id ? (
                  <div className="w-32 shrink-0">
                    <MediaPreview
                      source="resource"
                      kind="audio"
                      resourceVersionId={r.current_version_id}
                      size="inline"
                    />
                  </div>
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex h-10 w-10 items-center justify-center rounded bg-muted text-xs text-muted-foreground"
                  >
                    {kindGlyph('audio')}
                  </div>
                )}
                <span className="flex-1 text-sm font-medium">
                  {pickName(r.name_i18n, lang)}
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onSelect(r)}
                  disabled={r.current_version_id === null}
                  data-testid={`composer-bg-music-picker-select-${r.id}`}
                >
                  {t('composers:picker.select', { defaultValue: 'Sélectionner' })}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Audit drawer
// ---------------------------------------------------------------------------

export interface AuditDrawerProps {
  composerId: string;
  state: ResourceState;
}

// Exported (SP3c) so the tabbed ComposerEditor can render it as the Approbation
// tab. Audit (runAuditAction) is a lifecycle action, NOT a registered save.
export function AuditDrawer({ composerId, state }: AuditDrawerProps): JSX.Element {
  const { t } = useTranslation(['composers']);
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const [rationale, setRationale] = useState<string>('');
  const [messageKey, setMessageKey] = useState<string | null>(null);

  const visible = visibleAuditActionsForState(state);
  const allowed = visible.filter((a) => hasPermission(permissionForAction(a)));

  const mutation = useMutation<unknown, ApiError, ComposerAuditAction>({
    mutationFn: (action) =>
      runAuditAction(composerId, action, {
        ...(rationale.trim().length > 0 ? { rationale: rationale.trim() } : {}),
      }),
    onSuccess: () => {
      setMessageKey('composers:detail.audit.action_success');
      setRationale('');
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'detail', composerId],
      });
      void queryClient.invalidateQueries({ queryKey: ['composers', 'list'] });
    },
    onError: () => {
      setMessageKey('composers:detail.audit.action_error');
    },
  });

  return (
    <aside
      data-testid="composer-audit-drawer"
      aria-label={t('composers:detail.audit_drawer_title')}
      className="flex flex-col gap-3 rounded-md border border-border bg-card p-4"
    >
      <header>
        <h2 className="text-lg font-semibold">
          {t('composers:detail.audit_drawer_title')}
        </h2>
      </header>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">
          {t('composers:detail.audit.rationale_label')}
        </span>
        <textarea
          rows={2}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          className="rounded-md border border-input bg-background p-2 text-sm"
          data-testid="composer-audit-rationale"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        {allowed.length === 0 ? (
          <span
            data-testid="composer-audit-no-actions"
            className="text-sm text-muted-foreground"
          >
            {t('common:no_actions_available', { defaultValue: '—' })}
          </span>
        ) : (
          allowed.map((action) => (
            <Button
              key={action}
              type="button"
              variant={
                action === 'reject' || action === 'revoke'
                  ? 'destructive'
                  : 'default'
              }
              size="sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(action)}
              data-testid={`composer-audit-${action}`}
            >
              {t(`composers:detail.audit.${action}`)}
            </Button>
          ))
        )}
      </div>

      {messageKey && (
        <p
          role="status"
          data-testid="composer-audit-message"
          className={
            messageKey === 'composers:detail.audit.action_success'
              ? 'rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'
              : 'rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive'
          }
        >
          {t(messageKey)}
        </p>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Resource picker dialog — approved-only, kind ∈ {image,video,audio}
// ---------------------------------------------------------------------------

const PICKER_ELIGIBLE_KINDS = new Set(['image', 'video', 'audio']);

interface ResourcePickerDialogProps {
  onClose: () => void;
  onSelect: (resource: ResourceSummary) => void;
  lang: 'fr' | 'en';
}

function ResourcePickerDialog({
  onClose,
  onSelect,
  lang,
}: ResourcePickerDialogProps): JSX.Element {
  const { t } = useTranslation(['composers', 'resources']);

  const query = useQuery({
    queryKey: ['composers', 'picker', 'approved'],
    queryFn: () =>
      listResources({
        state: 'approved',
        page: 1,
        per_page: 100,
      }),
    staleTime: 15_000,
  });

  // Server's `kind` filter is single-valued, so we pull `state=approved` and
  // narrow to the three eligible kinds client-side.
  const items = (query.data?.items ?? []).filter((r) =>
    PICKER_ELIGIBLE_KINDS.has(r.kind),
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="composer-picker-dialog"
        className="flex max-h-[80vh] w-full max-w-2xl flex-col gap-3"
      >
        <DialogHeader>
          <DialogTitle>{t('composers:picker.title')}</DialogTitle>
        </DialogHeader>

        {query.isLoading ? (
          <p
            role="status"
            data-testid="composer-picker-loading"
            className="text-sm text-muted-foreground"
          >
            {t('composers:picker.loading')}
          </p>
        ) : items.length === 0 ? (
          <p
            data-testid="composer-picker-empty"
            className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
          >
            {t('composers:picker.empty')}
          </p>
        ) : (
          <ul
            className="flex flex-1 flex-col gap-2 overflow-auto"
            data-testid="composer-picker-list"
          >
            {items.map((r) => (
              <li
                key={r.id}
                data-testid={`composer-picker-item-${r.id}`}
                role="button"
                tabIndex={r.current_version_id === null ? -1 : 0}
                aria-disabled={r.current_version_id === null}
                onClick={() => {
                  if (r.current_version_id !== null) onSelect(r);
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && r.current_version_id !== null) {
                    e.preventDefault();
                    onSelect(r);
                  }
                }}
                className={`flex items-center gap-3 rounded-md border border-border bg-background p-2 ${
                  r.current_version_id !== null
                    ? 'cursor-pointer hover:bg-muted/50'
                    : 'opacity-50'
                }`}
              >
                <div
                  aria-hidden="true"
                  className="flex h-10 w-10 items-center justify-center rounded bg-muted text-xs text-muted-foreground"
                >
                  {kindGlyph(r.kind)}
                </div>
                <div className="flex flex-1 flex-col">
                  <span className="text-sm font-medium">
                    {pickName(r.name_i18n, lang)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t(`resources:kind.${r.kind}`)}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(r);
                  }}
                  disabled={r.current_version_id === null}
                  data-testid={`composer-picker-select-${r.id}`}
                >
                  {t('composers:picker.select')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tiny shared helpers
// ---------------------------------------------------------------------------

function kindGlyph(
  kind: 'image' | 'video' | 'audio' | 'rss_feed' | 'web_page',
): string {
  switch (kind) {
    case 'image':
      return 'IMG';
    case 'video':
      return 'VID';
    case 'audio':
      return 'AUD';
    case 'rss_feed':
      return 'RSS';
    case 'web_page':
      return 'WEB';
    default:
      return '·';
  }
}

export default ComposerDetailPage;
